import {z} from "zod/v4-mini"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {GeometryAttributeNode, pointGeometryAttributeLikeMarker} from "#template/nodes/geometry-graph/attribute/attribute-node"
import {createPointGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryValueData, GeometryValueRefData} from "#template/nodes/geometry-graph/interfaces"
import type {ScalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import {scalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {PointGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {GeomBuilderContext, ValueRef} from "#template/geometry-processing/geometry-graph"
import {Primitives} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type PointGeometryPointSet2Parameters = {
    data: {x: ScalarArg | null; y: ScalarArg | null}[]
}

const definition: TemplateNodeDefinition<PointGeometryPointSet2Parameters> = {
    nodeClass: "PointGeometryPointSet2",
    validation: {
        parameters: z.object({
            data: z.array(z.object({x: z.nullable(scalarArg), y: z.nullable(scalarArg)})),
        }),
        returns: getTemplateNodeReturns(),
    },
}

function emptyListToNull<T>(list: T[]): T[] | null {
    return list.length > 0 ? list : null
}
const resolveScalarArg = (value: GeometryValueData | number): GeometryValueRefData | number => (typeof value === "number" ? value : value.ref)

@registerTemplateGraphNode
export class PointGeometryPointSet2 extends GeometryAttributeNode<0, PointGeometryPointSet2Parameters> implements PointGeometryAttributeLikeNode {
    get [pointGeometryAttributeLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    protected override hasValidParameters(
        parameters: PointGeometryPointSet2Parameters,
    ): parameters is PointGeometryPointSet2Parameters & NonNullableParameters<PointGeometryPointSet2Parameters> {
        return parameters.data.length > 0
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<PointGeometryPointSet2Parameters>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<0> | null>; invalidator?: BuilderInlet<null>} {
        const {data} = parameters

        const [evaluatedDataValid, evalutedInputDataInvalid] = scope.branch(
            scope.pureLambda(
                scope.filterInvalid(
                    scope.list(
                        data
                            .filter((value): value is {x: ScalarArg; y: ScalarArg} => value.x !== null && value.y !== null)
                            .map((value, idx) =>
                                scope.pureLambda(
                                    scope.struct(`evaluatedInputDataEntry${idx}`, {
                                        x: evaluator.evaluate(value.x),
                                        y: evaluator.evaluate(value.y),
                                    }),
                                    (value) => (value.x === null || value.y === null ? null : {x: value.x, y: value.y}),
                                    `evaluatedValidInputDataEntry${idx}`,
                                ),
                            ),
                    ),
                ),
                emptyListToNull,
                "evaluatedInputData",
            ),
        )

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(evaluatedDataValid, ctx),
            ([data, ctx]) =>
                createPointGeometryAttributeData(
                    applyGeometryOperator(
                        (...args: [ValueRef | number, ValueRef | number][]) => Primitives.pointSet2(ctx, args),
                        ...data.map(
                            (value) => [resolveScalarArg(value.x), resolveScalarArg(value.y)] as [GeometryValueRefData | number, GeometryValueRefData | number],
                        ),
                    ),
                ),
            "createPrimitive",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: evalutedInputDataInvalid}
    }
}
